import { DiagramModel } from "@projectstorm/react-diagrams";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MainWindow from "./MainWindow";
import Context from "../store/context";
import { proxyAPI } from "../proxyAPI";
import {
  addNewWorkflow,
  closeProject,
  createNewMessageConfig,
  editAction,
  saveProject,
  selectProjectUUID,
  selectWorkflows,
  setProjectData,
} from "../store/slices/projectSlice";
import { setSendLists } from "../store/slices/sendListsSlice";
import { createEditorState, makeid, shortUUID } from "../helpers";
import { setSurveyThemes } from "../store/slices/surveyThemesSlice";
import FullScreenLoadingIndicator from "../Common/FullScreenLoadingIndicator";
import { useGetContactFieldsQuery } from "../store/slices/contactsSlice";
import {
  useGetCommsSettingsDataQuery,
  useGetSystemDataStructuresQuery,
} from "../store/slices/settingsSlice";

// page props
const { productName } = JSON.parse(
  document.querySelector("#app").dataset.page
).props;

// single workflow
const newWorkflow = (IsEntrypoint = false) => ({
  Label: "New workflow",
  UUID: uuidv4(),
  IsEntrypoint,
  EventListeners: [],
});

const getActionUuidFromPortName = (portName) => {
  if (!portName) return null;
  // check pipe
  if (portName.includes("|")) {
    return portName.split("|")[1];
  }
  // check length for old versions - both have long uuid
  if (portName.length === 73) {
    return portName.slice(37);
  }
  // if 71 both from backend - both have short uuid
  if (portName.length === 71) {
    return portName.slice(36);
  }
  //if 72 check last two character
  if (portName.length === 72) {
    if (portName.slice(-2) === "-s") {
      // second part is short uuid with -s at the end
      return portName.slice(37);
    } else {
      // first part is short
      return portName.slice(36);
    }
  }
};

export default function AdvancedBuilder({ engine, dagreEngine }) {
  const { data: commsSettings } = useGetCommsSettingsDataQuery();

  // new project
  const newProjectData = () => {
    const newMessageConfigUUID = uuidv4();

    return {
      Label: "New Project",
      UUID: shortUUID(),
      Status: "draft",
      Reference: makeid(8),
      Workflows: [newWorkflow(true)],
      EventHandlers: [],
      BusinessHours: [],
      MessageTemplates: [],
      InputArguments: [],
      MessageConfigUUID: newMessageConfigUUID,
      MessageConfigs: [
        createNewMessageConfig({
          UUID: newMessageConfigUUID,
          Label: "Default message config",
          commsSettings,
        }),
      ],
    };
  };

  // inertia props
  const params = useParams();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // global state (to be passed in context)
  const {
    data: contactFields = [],
    isFetching,
    isLoading,
  } = useGetContactFieldsQuery();
  const [reportingTopics, setreportingTopics] = useState([]);
  const [reportingTemplates, setReportingTemplates] = useState([]);
  const [apiProfiles, setApiProfiles] = useState([]);
  const [components, setComponents] = useState([]);
  const [jsonCollections, setJsonCollections] = useState([]);
  const [isModalSidebar, setIsModalSidebar] = useState(false);

  // workflows data
  const projectUUID = useSelector(selectProjectUUID);
  const [projectLoading, setProjectLoading] = useState(false);

  const save = () => dispatch(saveProject({ navigate, engine }));

  // zoom to fit handler
  const zoomToFit = () => {
    engine.zoomToFitNodes({ margin: 10, maxZoom: 1 });
  };

  // autoarrange handler
  const autoArrange = () => {
    dagreEngine.redistribute(engine.model);
    zoomToFit();
  };

  // add a child node to a parent node
  // if no parent, add the node to the canvas
  const addChild = (data = { workflow: newWorkflow() }, parentUUID) => {
    dispatch(addNewWorkflow(data.workflow));
    // create the node model using the factory
    const newNode = engine.nodeFactories
      .getFactory("logic")
      .generateModel(data);
    // add the node to the model
    engine.model.addNode(newNode);
    // if there is a parent node, create a link
    newNode.setSelected();
    if (parentUUID) {
      const parentNode = engine.model.getNode(parentUUID);
      newNode.setPosition(parentNode.position.x + 1000, parentNode.position.y);
    }
    // show the available nodes that can connect the new node
    engine.model.clearSelection();
    engine.repaintCanvas();
    setTimeout(() => {
      zoomToFit();
    }, 100);
    return newNode;
  };

  // initialize project data
  const initializeWorkflowNodes = (workflows) => {
    if (workflows.length > 0) {
      engine.setModel(new DiagramModel());
      engine.model.registerListener({
        linksUpdated: (event) => {
          event.link.registerListener({
            targetPortChanged: (e) => {
              const targetUUID = e.entity.targetPort.parent.options.id;
              const actionUUID = getActionUuidFromPortName(
                e.entity.sourcePort.options.name
              );
              dispatch(
                editAction({
                  actionUUID,
                  field: "TypeUUID",
                  value: targetUUID,
                })
              );
            },
          });
        },
      });
      setTimeout(() => {
        workflows.forEach((workflow) =>
          addChild({
            workflow,
          })
        );
        setTimeout(() => {
          autoArrange();
        }, 200);
      }, 100);
    }
  };

  const projectWorkflows = useSelector(selectWorkflows);
  const resetLayout = () => {
    initializeWorkflowNodes(projectWorkflows);
  };

  // double click to zoom handler
  let zoomedInNode = null;
  const zoomToSelected = () => {
    if (
      zoomedInNode === null ||
      zoomedInNode !== engine.model.getSelectedEntities()[0]
    ) {
      engine.zoomToFitSelectedNodes({ margin: 10, maxZoom: 2 });
      [zoomedInNode] = engine.model.getSelectedEntities();
    } else {
      engine.zoomToFitNodes({ margin: 10, maxZoom: 1 });
      zoomedInNode = null;
    }
  };

  // project loader
  const projectLoader = async () => {
    setProjectLoading(true);
    let projectData = null;

    if (params.projectID === undefined) {
      return null;
    }

    // new project
    if (params.projectID === "new") {
      if (commsSettings) {
        projectData = newProjectData();
      }
    } else {
      // get project data
      projectData = await proxyAPI({
        prefix: "logic/workflows/api",
        url: `/project/${params.projectID}`,
        setLoading: () => null,
      });
    }

    // get themes
    const themes = await proxyAPI({
      prefix: "logic/workflows/api",
      url: `/themes`,
      setLoading: () => null,
    });

    // get send lists for templates and workflows
    const sendLists = await Promise.all(
      [
        ...(projectData?.MessageTemplates ?? []),
        ...(projectData?.Workflows || []),
      ]
        .filter((mt) => mt.ContactSetUUID === "yes")
        .map((mt) =>
          proxyAPI({ prefix: "contacts/v1", url: `/send-list/${mt.UUID}` })
        )
    ).then((values) => values.filter((v) => v));
    setTimeout(() => {
      setProjectLoading(false);
    }, 400);
    return { projectData, themes, sendLists };
  };

  // load project
  useEffect(() => {
    if (!params.projectID) {
      dispatch(closeProject());
      return;
    }
    if (params.projectID) {
      (async () => {
        const loaderData = await projectLoader();

        dispatch(setProjectData(loaderData.projectData));
        dispatch(setSendLists(loaderData.sendLists));
        dispatch(setSurveyThemes(loaderData.themes));
        if (loaderData.projectData.ComponentConfig?.Layout) {
          const newModel = new DiagramModel();
          newModel.deserializeModel(
            loaderData.projectData.ComponentConfig.Layout,
            engine
          );
          newModel.registerListener({
            linksUpdated: (event) => {
              event.link.registerListener({
                targetPortChanged: (e) => {
                  const targetUUID = e.entity.targetPort.parent.options.id;
                  const actionUUID = getActionUuidFromPortName(
                    e.entity.sourcePort.options.name
                  );
                  dispatch(
                    editAction({
                      actionUUID,
                      field: "TypeUUID",
                      value: targetUUID,
                    })
                  );
                },
              });
            },
          });
          engine.setModel(newModel);
        } else {
          initializeWorkflowNodes(loaderData.projectData.Workflows);
        }
      })();
    }
    return () => {
      dispatch(closeProject());
    };
  }, [params.projectID, commsSettings]);

  // update contactfields
  useEffect(() => {
    if (params.workflowUUID) {
      proxyAPI({
        prefix: "logic/reporting",
        url: "/topics?pagination.perPage=1000",
      }).then((data) => setreportingTopics(data.data));
      proxyAPI({
        prefix: "logic/reporting",
        url: "/templates?pagination.perPage=1000",
      }).then((data) => setReportingTemplates(data.data));
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `api-profiles`,
      }).then((data) => {
        if (data?.profiles) {
          setApiProfiles(data.profiles);
        }
      });
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `json-api-data-collections`,
      }).then((data) => {
        if (data?.collections) {
          setJsonCollections(data.collections);
        }
      });
      proxyAPI({
        prefix: "logic/workflows/api",
        url: `list-projects?withParams=true&offset=0&limit=100`,
      }).then((data) => {
        if (data?.projects) {
          setComponents(data.projects);
        }
      });
    }
  }, [params.workflowUUID]);

  const { data: systemDataStructures } = useGetSystemDataStructuresQuery();

  return (
    <>
      <FullScreenLoadingIndicator loading={projectLoading} />
      {projectUUID ? (
        <Context.Provider
          // eslint-disable-next-line react/jsx-no-constructed-context-values
          value={{
            addChild,
            engine,
            isModalSidebar,
            setIsModalSidebar,
            saveProject: save,
            contactFields,
            reportingTopics,
            reportingTemplates,
            apiProfiles,
            jsonCollections,
            systemDataStructures,
            components,
          }}
        >
          <MainWindow
            redistribute={autoArrange}
            zoomToFit={zoomToFit}
            zoomToSelected={zoomToSelected}
            resetLayout={resetLayout}
          />
        </Context.Provider>
      ) : (
        <div className="alert alert-warning text-center container my-5">
          Project not found.{" "}
          <Link to="/library/logic/builder/new/settings">Create new</Link>
        </div>
      )}
    </>
  );
}
